import { z } from "zod";
import { isoDateSchema, isoMonthSchema } from "../dates";
import {
  accountIdSchema,
  budgetEntryIdSchema,
  categoryIdSchema,
  importBatchIdSchema,
  masterCategoryIdSchema,
  memoryIdSchema,
  importRowIdSchema,
  payeeIdSchema,
  payeeRuleIdSchema,
  scheduledIdSchema,
  transactionIdSchema,
} from "../ids";

export const centsSchema = z.number().int().safe();

const base = {
  deleted: z.boolean().default(false),
};

export const accountKinds = [
  "checking",
  "savings",
  "creditCard",
  "cash",
  "loan",
  "asset",
  "liability",
] as const;
export const accountKindSchema = z.enum(accountKinds);
export type AccountKind = z.infer<typeof accountKindSchema>;

export const csvProfileSchema = z.object({
  hasHeader: z.boolean(),
  dateColumn: z.number().int().min(0),
  dateFormat: z.enum(["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "D MMM YYYY"]),
  amountColumn: z.number().int().min(0).optional(),
  debitColumn: z.number().int().min(0).optional(),
  creditColumn: z.number().int().min(0).optional(),
  invertSign: z.boolean().default(false),
  descriptionColumns: z.array(z.number().int().min(0)).min(1),
  skipLines: z.number().int().min(0).default(0),
});
export type CsvProfile = z.infer<typeof csvProfileSchema>;

export const accountSchema = z.object({
  id: accountIdSchema,
  name: z.string().min(1).max(100),
  kind: accountKindSchema,
  onBudget: z.boolean(),
  closed: z.boolean().default(false),
  sortOrder: z.number().default(0),
  note: z.string().max(2000).default(""),
  importProfile: csvProfileSchema.optional(),
  externalIds: z.array(z.string().max(100)).default([]),
  loan: z
    .object({ ratePct: z.number().min(0).max(100), offsetAccountId: accountIdSchema.optional() })
    .optional(),
  ...base,
});
export type Account = z.infer<typeof accountSchema>;

export const masterCategorySchema = z.object({
  id: masterCategoryIdSchema,
  name: z.string().min(1).max(100),
  sortOrder: z.number().default(0),
  hidden: z.boolean().default(false),
  ...base,
});
export type MasterCategory = z.infer<typeof masterCategorySchema>;

export const overspendModeSchema = z.enum(["reset", "carry"]);
export type OverspendMode = z.infer<typeof overspendModeSchema>;

export const categorySchema = z.object({
  id: categoryIdSchema,
  masterCategoryId: masterCategoryIdSchema,
  name: z.string().min(1).max(100),
  sortOrder: z.number().default(0),
  hidden: z.boolean().default(false),
  overspend: overspendModeSchema.default("reset"),
  note: z.string().max(2000).default(""),
  ...base,
});
export type Category = z.infer<typeof categorySchema>;

export const budgetEntrySchema = z.object({
  id: budgetEntryIdSchema,
  categoryId: categoryIdSchema,
  month: isoMonthSchema,
  amount: centsSchema,
  note: z.string().max(2000).default(""),
  ...base,
});
export type BudgetEntry = z.infer<typeof budgetEntrySchema>;

export const monthNoteSchema = z.object({
  id: isoMonthSchema,
  note: z.string().max(4000).default(""),
  ...base,
});
export type MonthNote = z.infer<typeof monthNoteSchema>;

export const payeeSchema = z.object({
  id: payeeIdSchema,
  name: z.string().min(1).max(100),
  ...base,
});
export type Payee = z.infer<typeof payeeSchema>;

export const payeeRuleMatchSchema = z.enum(["exact", "contains", "startsWith", "regex"]);
export const payeeRuleSchema = z.object({
  id: payeeRuleIdSchema,
  match: payeeRuleMatchSchema,
  pattern: z.string().min(1).max(200),
  payeeId: payeeIdSchema,
  categoryId: categoryIdSchema.optional(),
  income: z.enum(["thisMonth", "nextMonth"]).optional(),
  transferAccountId: accountIdSchema.optional(),
  memo: z.string().max(500).optional(),
  /** Inclusive range on the signed amount, for rules that differ only by amount. */
  amountMin: centsSchema.optional(),
  amountMax: centsSchema.optional(),
  priority: z.number().int().default(0),
  ...base,
});
export type PayeeRule = z.infer<typeof payeeRuleSchema>;

export const incomeSchema = z.enum(["thisMonth", "nextMonth"]);

/**
 * Income choices in the order every picker lists them. Next month comes first because it's the
 * default: this month's spending is funded by last month's income (SPEC §4).
 */
export const incomeChoices = ["nextMonth", "thisMonth"] as const;
export type Income = z.infer<typeof incomeSchema>;

export const splitLineSchema = z.object({
  amount: centsSchema,
  categoryId: categoryIdSchema.optional(),
  income: incomeSchema.optional(),
  transferAccountId: accountIdSchema.optional(),
  /** The counterpart transaction created for a transfer line. */
  transferTxnId: transactionIdSchema.optional(),
  payeeId: payeeIdSchema.optional(),
  memo: z.string().max(500).default(""),
});
export type SplitLine = z.infer<typeof splitLineSchema>;

export const amountAndSplitsSchema = z.object({
  amount: centsSchema,
  splits: z.array(splitLineSchema).default([]),
});
export type AmountAndSplits = z.infer<typeof amountAndSplitsSchema>;

export const clearedSchema = z.enum(["uncleared", "cleared", "reconciled"]);
export type Cleared = z.infer<typeof clearedSchema>;

export const flags = ["none", "red", "orange", "yellow", "green", "blue", "purple"] as const;
export const flagSchema = z.enum(flags);
export type Flag = z.infer<typeof flagSchema>;

export const importNoteSchema = z.object({
  text: z.string().max(4000),
  evidence: z.array(z.string().max(1000)).default([]),
  author: z.string().max(100),
  batchId: importBatchIdSchema.optional(),
});
export type ImportNote = z.infer<typeof importNoteSchema>;

export const locationSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type GeoLocation = z.infer<typeof locationSchema>;

export const transactionSchema = z.object({
  id: transactionIdSchema,
  accountId: accountIdSchema,
  date: isoDateSchema,
  amountAndSplits: amountAndSplitsSchema,
  payeeId: payeeIdSchema.optional(),
  categoryId: categoryIdSchema.optional(),
  income: incomeSchema.optional(),
  /** Set on both sides of a transfer. For a split-line counterpart, `txnId` is the parent. */
  transfer: z.object({ accountId: accountIdSchema, txnId: transactionIdSchema }).optional(),
  memo: z.string().max(500).default(""),
  cleared: clearedSchema.default("uncleared"),
  flag: flagSchema.default("none"),
  approved: z.boolean().default(true),
  importKey: z.string().max(64).optional(),
  importBatchId: importBatchIdSchema.optional(),
  scheduleId: scheduledIdSchema.optional(),
  occurrence: isoDateSchema.optional(),
  importNote: importNoteSchema.optional(),
  location: locationSchema.optional(),
  createdBy: z.string().max(100).optional(),
  ...base,
});
export type Transaction = z.infer<typeof transactionSchema>;

export const frequencies = [
  "once",
  "weekly",
  "every2Weeks",
  "twiceAMonth",
  "every4Weeks",
  "monthly",
  "every2Months",
  "every3Months",
  "every4Months",
  "twiceAYear",
  "yearly",
  "every2Years",
] as const;
export const frequencySchema = z.enum(frequencies);
export type Frequency = z.infer<typeof frequencySchema>;

export const scheduledSchema = z.object({
  id: scheduledIdSchema,
  accountId: accountIdSchema,
  amountAndSplits: amountAndSplitsSchema,
  payeeId: payeeIdSchema.optional(),
  categoryId: categoryIdSchema.optional(),
  income: incomeSchema.optional(),
  transferAccountId: accountIdSchema.optional(),
  memo: z.string().max(500).default(""),
  flag: flagSchema.default("none"),
  frequency: frequencySchema,
  /** The first date of the series; occurrences are generated from here. */
  startDate: isoDateSchema,
  nextDate: isoDateSchema,
  endDate: isoDateSchema.optional(),
  ...base,
});
export type Scheduled = z.infer<typeof scheduledSchema>;

export const memorySchema = z.object({
  id: memoryIdSchema,
  text: z.string().min(1).max(4000),
  author: z.string().max(100),
  createdAt: z.string(),
  txnIds: z.array(transactionIdSchema).default([]),
  payeeId: payeeIdSchema.optional(),
  categoryId: categoryIdSchema.optional(),
  amount: centsSchema.optional(),
  date: isoDateSchema.optional(),
  /** The import row it was written about during review; commit adds that row's transaction. */
  importRowId: importRowIdSchema.optional(),
  ...base,
});
export type Memory = z.infer<typeof memorySchema>;

export const entitySchemas = {
  accounts: accountSchema,
  masterCategories: masterCategorySchema,
  categories: categorySchema,
  budgetEntries: budgetEntrySchema,
  monthNotes: monthNoteSchema,
  payees: payeeSchema,
  payeeRules: payeeRuleSchema,
  transactions: transactionSchema,
  scheduled: scheduledSchema,
  memories: memorySchema,
} as const;

export type EntityType = keyof typeof entitySchemas;
export const entityTypes = Object.keys(entitySchemas) as EntityType[];

export interface EntityByType {
  accounts: Account;
  masterCategories: MasterCategory;
  categories: Category;
  budgetEntries: BudgetEntry;
  monthNotes: MonthNote;
  payees: Payee;
  payeeRules: PayeeRule;
  transactions: Transaction;
  scheduled: Scheduled;
  memories: Memory;
}

export { isoDateSchema, isoMonthSchema };
